import type { ForwardRefExoticComponent, RefAttributes } from "react";
import { Check, Circle, MessageSquare, Loader, type LucideProps } from "lucide-react";
import { StateIndicatorVariantTypes } from "../types/stateIndicatorTypes";

export type StateIndicatorVariantType = typeof StateIndicatorVariantTypes[keyof typeof StateIndicatorVariantTypes];

type StateIndicatorProps = {
    compact?: boolean;
    hasIcon?: boolean;
    Icon?: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>;
} & (
    | {
        variant?: never;
        text?: never;
        isSuccessful: boolean;
        successText: string;
        warningText: string;
    }
    | {
        variant: StateIndicatorVariantType;
        text: string;
        isSuccessful?: never;
        successText?: never;
        warningText?: never;
    }
);

const variantStyles: Record<StateIndicatorVariantType, { container: string; text: string }> = {
    [StateIndicatorVariantTypes.NEUTRAL]: {
        container: "text-bg-light border",
        text: "text-muted"
    },
    [StateIndicatorVariantTypes.INFO]: {
        container: "k-bg-brand-info !k-border-brand-info",
        text: "k-text-brand-info fw-semibold"
    },
    [StateIndicatorVariantTypes.WARNING]: {
        container: "!k-bg-brand-warning-subtle !k-border-brand-warning-subtle",
        text: "k-text-brand-warning"
    },
    [StateIndicatorVariantTypes.SUCCESS]: {
        container: "!k-bg-brand-teal-subtle !k-border-brand-teal-subtle",
        text: "k-text-brand-teal"
    },
    [StateIndicatorVariantTypes.ERROR]: {
        container: "",
        text: ""
    }
};

const StateIndicator = (props: StateIndicatorProps) => {
    const { Icon, compact = false } = props;
    const sizeClass = compact ? "px-2 py-1 gap-1" : "px-3 py-2 gap-2";
    const textSizeClass = compact ? "fs-8 fw-medium" : "text-xs";

    if ("isSuccessful" in props && props.isSuccessful !== undefined) {
        const { isSuccessful, successText, warningText, hasIcon = true } = props;
        const variant: StateIndicatorVariantType = isSuccessful ? "success" : "warning";
        const { container, text } = variantStyles[variant];

        return (
            <div className={`d-inline-flex align-items-center badge rounded-pill fw-medium ${sizeClass} ${container}`}>
                {isSuccessful && hasIcon
                    ? <Check size={compact ? 12 : 14} strokeWidth={3} className="k-text-brand-teal" />
                    : <Circle size={compact ? 12 : 14} strokeWidth={3} className="k-text-brand-warning" />}
                <span className={`${textSizeClass} font-medium ${text}`}>{isSuccessful ? successText : warningText}</span>
            </div>
        );
    }

    const { variant, text, hasIcon = true } = props;
    const { container, text: textStyle } = variantStyles[variant];

    return (
        <div className={`d-inline-flex align-items-center badge rounded-pill fw-medium min-w-0 ${sizeClass} ${container}`}>
            {variant === "success" && hasIcon
                && (Icon
                    ? <Icon size={compact ? 12 : 14} strokeWidth={3} className="k-text-brand-teal flex-shrink-0" />
                    : <Check size={compact ? 12 : 14} strokeWidth={3} className="k-text-brand-teal flex-shrink-0" />)}

            {variant === "info" && hasIcon
                && (
                    <span className="icon-rotate-infinite">
                        <Loader size={compact ? 12 : 14} strokeWidth={2.5} type="infinite-spinner" className="k-text-brand-info flex-shrink-0" />
                    </span>
                )}

            {variant === "neutral" && hasIcon
                && (Icon
                    ? <Icon size={compact ? 12 : 14} strokeWidth={3} className="text-muted flex-shrink-0" />
                    : <MessageSquare size={compact ? 12 : 14} strokeWidth={2.5} className="text-muted flex-shrink-0" />)}

            {variant === "warning" && hasIcon
                && (Icon
                    ? <Icon size={compact ? 12 : 14} strokeWidth={3} className="k-text-brand-warning flex-shrink-0" />
                    : <Circle size={compact ? 12 : 14} strokeWidth={3} className="k-text-brand-warning flex-shrink-0" />)}

            <span className={`${textSizeClass} font-medium ${textStyle} text-truncate`}>{text}</span>
        </div>
    );
};

export default StateIndicator;
